import React, { useContext, useEffect, useMemo, useState } from 'react';
import { generatePath } from 'react-router-dom';
import { connect } from 'react-redux';

// Store
import { Dispatch, RootState } from '../../store/store';

// Contexts
import { AbilityContext } from '../Can/Can';

// Enums
import { Action, ChangeChannelAction } from '../../enums/action';
import SortType from '../../enums/sort-type.enum';

// Constants
import { PAGE_TITLE } from '../../constants/common';
import ROUTE from '../../constants/route-constants';

// Types
import { ButtonType } from '../../types/button-type';
import { ElementSize } from '../../types/element-size';

// Components
import InteractiveButton from '../InteractiveButton/InteractiveButton';
import CycleTable from '../CycleTable/CycleTable/CycleTable';
import CycleActions, { CycleAction } from '../CycleActions/CycleActions';
import Modal from '../Modal/Modal';

// Icons
import IconCycle from '../Icons/IconCycle/IconCycle';
import IconBin from '../Icons/IconBin/IconBin';
import IconPlay from '../Icons/IconPlay/IconPlay';
import IconPaused from '../Icons/IconPaused/IconPaused';
import IconCrossPaused from '../Icons/IconCrossPaused/IconCrossPaused';
import IconStop from '../Icons/IconStop/IconStop';
import IconGearFilled from '../Icons/IconGearFilled/IconGear';
import IconEditOutlined from '../Icons/IconEditOutlined/IconEditOutlined';
import IconCopy from '../Icons/IconCopy/IconCopy';
import CycleSettings from '../CycleSettings/CycleSettings';
import CycleAttachment from '../CycleAttachment/CycleAttachment';
import DuplicateCycleModal from '../DuplicateCycleModal/DuplicateCycleModal';
import { Cycle as CycleType } from '../../store/models/cycles';
import SpinnerLoader from '../SpinnerLoader/SpinnerLoader';
import checkChannelAvailability from '../../utils/checkChannelAvailability';
import getMeasurementPreference from '../../utils/getMeasurementPreference';
import { CycleOperationsService } from '../../store/http/cycle-operations.service';
import { useTranslation } from 'react-i18next';
import CycleManualSettingField from '../CycleManualSettingField/CycleManualSettingField';
import { ChannelActionItem } from '../../store/models/channels';
import getChannelActionItems from './utils/getChannelActionItems';
import { CycleItem } from './types';
import Tooltip from '../Tooltip';
import { changeChannelAction } from '../../pages/EditController/SubPages/EditChannel/channelActions';
import { CHANNEL_ACTIONS_BY_ROLE } from '../../constants/channel-allowed-actions';
import useEditModeEnabled from '../../hooks/useEditModeEnabled';
import { useCalCertGuardedAction } from '../../hooks/useCalCertGuardedAction';
import CalCertWarningModal from '../CalCertWarningModal/CalCertWarningModal';


type ComponentProps = {
  cycle: CycleItem;
  sort: SortType;
  isViewActions: boolean;
  clientId: string;
  contractId: string;
  screenId: string;
  attachmentSaving: boolean;
};

type DispatchProps = ReturnType<typeof mapDispatch>;
type StateProps = ReturnType<typeof mapState>;

type CycleProps = ComponentProps & DispatchProps & StateProps;

const CycleV2: React.FC<CycleProps> = ({
  cycle,
  deleteCycle,
  getCycles,
  sort,
  isViewActions,
  selectCycle,
  clientId,
  contractId,
  screenId,
  saveAttachment,
  getAttachments,
  setIsViewSettingsGlobal,
  measurement,
  updateChannels,
  updateCycle,
  updateController,
  user,
}) => {
  const ability = useContext(AbilityContext);
  const isEditModeEnabled = useEditModeEnabled();

  const { t } = useTranslation();

  const {
    run: runCalCertGuardedAction,
    missingControllers: missingCalCertControllers,
    closeModal: closeCalCertModal,
    disableCheckAndRetry: disableCalCertCheckAndRetry,
    proceedWithoutCalCert,
  } = useCalCertGuardedAction(updateController);

  const allowedChannelActions = useMemo(() => {
    if (!user?.memberData?.role) {
      return [];
    }

    return CHANNEL_ACTIONS_BY_ROLE[user.memberData.role];
  }, [user]);

  const canResetAllFaults = allowedChannelActions.includes(ChangeChannelAction.RESET_CHANNEL_FAULTS);
  const canTestChannel = allowedChannelActions.includes(ChangeChannelAction.TEST_CHANNEL);

  const pathToViewCycle = generatePath(ROUTE.CYCLE, {
    clientId,
    contractId,
    screenId,
    id: cycle.id,
  });
  const pathToEditCycle = generatePath(ROUTE.EDIT_CYCLE, {
    clientId,
    contractId,
    screenId,
    id: cycle.id,
  });

  const [isDeletionCycle, setIsDeletionCycle] = useState<boolean>(false);
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState<boolean>(false);
  const [isAttachmentModalOpen, setIsAttachmentModalOpen] = useState<boolean>(false);
  const [isViewSettings, setIsViewSettings] = useState<boolean>(false);
  const [selectedAttachment, setSelectedAttachment] = useState<File | null>(null);
  const [attachmentSaving, setAttachmentSaving] = useState<boolean>(false);

  const [faultChannelsByController, setFaultChannelsByController] = useState<ChannelActionItem[] | null>(null);

  const canEditCycle = useMemo(() => {
    return ability.can(Action.Read, PAGE_TITLE.EDIT_CYCLE);
  }, [ability]);

  const isDisabledFaultChannels = useMemo(() => {
    return !faultChannelsByController || faultChannelsByController.length === 0;
  }, [faultChannelsByController]);

  const getFaultChannels = (cycle: CycleItem) => {
    const items = getChannelActionItems(cycle.cycle_controllers, cycle.iotDataResponse, cycle.controllersSettings);

    setFaultChannelsByController(items);
  };

  useEffect(() => {
    const isFetchData = cycle ? checkChannelAvailability(cycle.cycle_controllers) : false;
    if (isFetchData) {
      getFaultChannels(cycle);
    }
  }, [cycle]);

  useEffect(() => {
    setIsViewSettingsGlobal(isViewSettings);
  }, [isViewSettings]);

  const CYCLE_ACTIONS: CycleAction[] = [
    {
      name: t('commonText.play'),
      icon: <IconPlay size={ElementSize.XXS} iconClassName="py-1" />,
      onClick: () => runCalCertGuardedAction((options) => CycleOperationsService.startCycle(cycle.id, options)),
    },
    {
      name: t('commonText.pause'),
      icon: <IconPaused size={ElementSize.XXS} iconClassName="py-1" />,
      onClick: () => CycleOperationsService.pauseCycle(cycle.id),
    },
    {
      name: t('commonText.unpause'),
      icon: <IconCrossPaused size={ElementSize.XXS} iconClassName="py-1" width={24} />,
      onClick: () => CycleOperationsService.unpauseCycle(cycle.id),
    },
    {
      name: t('commonText.stop'),
      icon: <IconStop size={ElementSize.XXS} iconClassName="py-1" />,
      onClick: () => CycleOperationsService.stopCycle(cycle.id),
    },
    {
      name: t('commonText.cycleSettings'),
      icon: <IconGearFilled size={ElementSize.XXS} iconClassName="py-1" />,
      onClick: () => openSettings(),
    },
  ];

  const saveCycleAttachment = async () => {
    if (selectedAttachment) {
      setAttachmentSaving(true);
      try {
        await saveAttachment({ id: cycle.id, attachment: selectedAttachment });
      } finally {
        setAttachmentSaving(false);
      }
    }
  };

  const openSettings = () => {
    const isAccess = cycle ? checkChannelAvailability(cycle.cycle_controllers) : false;

    if (isAccess) {
      setIsViewSettings(true);
      selectCycle(cycle as unknown as CycleType);
    }
  };

  const closeSettings = () => {
    setIsViewSettings(false);
    selectCycle(null);
  };

  const onDelete = async () => {
    await deleteCycle(cycle.id);
    await getCycles({ screenId, contractId, query: { sort } });
  };

  const acceptDeleteCycle = () => {
    onDelete();
    closeModalDeletion();
  };

  const openModalDeletion = () => {
    setIsDeletionCycle(true);
  };

  const closeModalDeletion = () => {
    setIsDeletionCycle(false);
  };

  const onAttachmentModalClose = () => {
    setSelectedAttachment(null);
    setIsAttachmentModalOpen(false);
  };

  const resetFaultsForAllChannels = async (items: ChannelActionItem[]) => {
    await updateChannels({
      cycleId: cycle.id,
      data: { action: ChangeChannelAction.RESET_CHANNEL_FAULTS, list: items },
    });
  };

  const testChannel = async (controllerId: string, channelNum: number) => {
    await changeChannelAction(controllerId, channelNum, ChangeChannelAction.TEST_CHANNEL, '');
  };

  useEffect(() => {
    if (isAttachmentModalOpen) {
      getAttachments(cycle.id);
    }
  }, [cycle.id, isAttachmentModalOpen]);

  const cycleAutoSettingFields = [
    {
      title: t('labels.autoSPAdjust'),
      value: cycle.autoSPAdjust,
      onChange: (value) => updateCycle({ id: cycle.id, autoSPAdjust: value }),
    },
    {
      title: t('labels.autoPause'),
      value: cycle.autoPause,
      onChange: (value) => updateCycle({ id: cycle.id, autoPause: value }),
    },
    {
      title: t('labels.autoResetTimers'),
      value: cycle.autoResetTimers,
      onChange: (value) => updateCycle({ id: cycle.id, autoResetTimers: value }),
    },
  ];

  return (
    <>
      <div className="c-cycle relative flex flex-auto flex-col overflow-hidden rounded-lg border border-foggy-mist bg-white p-3 shadow-15 dark:border-strokedark dark:bg-boxdark">
        {isViewActions && canEditCycle && (
          <div className="flex items-center">
            <CycleActions actions={CYCLE_ACTIONS} />
            <div className="m-auto flex items-center gap-4 lg:ml-5 lg:gap-2">
              {cycleAutoSettingFields.map((field, index) => (
                <CycleManualSettingField key={`settingField-${index}`} field={field} />
              ))}
            </div>
          </div>
        )}
        <div className="c-cycle__header min-xsm:flex-row flex w-full items-center justify-between gap-x-2 pb-1 max-xsm:flex-col">
          <span className="c-cycle__name text-sm font-bold">
            {t('terms.cycle.nameCustom', '', { name: cycle.name })}
          </span>
          <div className="c-cycle__actions flex h-full flex-row items-center gap-x-2 max-xsm:justify-between">
            {ability.can(Action.Manage, PAGE_TITLE.CYCLE_ATTACHMENTS) && (
              <InteractiveButton
                size={ElementSize.SM}
                type={ButtonType.PrimaryInverse}
                text={t('buttons.attachments')}
                onClick={() => setIsAttachmentModalOpen(true)}
              />
            )}
            <InteractiveButton
              size={ElementSize.SM}
              type={ButtonType.PrimaryInverse}
              iconLeft={<IconCycle size={ElementSize.XXS} />}
              text={t('buttons.viewChart')}
              as="a"
              href={pathToViewCycle}
              target="_blank"
            />
            {canResetAllFaults && (
              <InteractiveButton
                size={ElementSize.SM}
                type={ButtonType.PrimaryInverse}
                text="Reset all faults"
                disabled={isDisabledFaultChannels}
                onClick={() => resetFaultsForAllChannels(faultChannelsByController)}
              />
            )}
            <div className="flex h-full flex-row gap-x-1">
              {ability.can(Action.Read, PAGE_TITLE.EDIT_CYCLE) && (
                <Tooltip hint={t('commonText.edit')} hintClassName='left-[50%] bottom-10'>
                  <a
                    className={`flex h-full w-9 flex-auto items-center justify-center rounded border border-foggy-mist dark:border-strokedark ${isEditModeEnabled ? 'cursor-pointer hover:border-red active:text-red' : 'cursor-not-allowed opacity-50'}`}
                    href={isEditModeEnabled ? pathToEditCycle : undefined}
                    onClick={(event) => { if (!isEditModeEnabled) event.preventDefault(); }}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <IconEditOutlined size={ElementSize.XXS} stroke="inherit" iconClassName="text-inherit dark:text-white" height={16} />
                  </a>
                </Tooltip>
              )}

              {ability.can(Action.Create, PAGE_TITLE.ADD_CYCLE) && (
                <Tooltip hint="Multi-duplicate" hintClassName='left-[50%] bottom-10'>
                  <div
                    className={`flex h-full w-9 flex-auto items-center justify-center rounded border border-foggy-mist dark:border-strokedark ${isEditModeEnabled ? 'cursor-pointer hover:border-red active:text-red' : 'cursor-not-allowed opacity-50'}`}
                    onClick={() => isEditModeEnabled && setIsDuplicateModalOpen(true)}
                  >
                    <IconCopy size={ElementSize.XXS} fill="inherit" iconClassName="text-inherit dark:text-white" height={16} />
                  </div>
                </Tooltip>
              )}

              {ability.can(Action.Delete, PAGE_TITLE.EDIT_CYCLE) && (
                <Tooltip hint={t('commonText.delete')} hintClassName='left-[50%] bottom-10'>
                  <div
                    className={`flex h-full w-9 flex-auto items-center justify-center rounded border border-foggy-mist dark:border-strokedark ${isEditModeEnabled ? 'cursor-pointer hover:border-red active:text-red' : 'cursor-not-allowed opacity-50'}`}
                    onClick={() => isEditModeEnabled && openModalDeletion()}
                  >
                    <IconBin size={ElementSize.XXS} fill="inherit" iconClassName="text-inherit dark:text-white" height={16} />
                  </div>
                </Tooltip>
              )}
            </div>
          </div>
        </div>
        {!measurement ? (
          <div className="flex w-full items-center justify-center py-7">
            <SpinnerLoader className="h-10 w-10" />
          </div>
        ) : (
          <CycleTable
            cycleId={cycle.id}
            cycle_controllers={cycle.cycle_controllers}
            iotDataResponse={cycle.iotDataResponse}
            deviceNames={cycle.deviceNames}
            controllersSettings={cycle.controllersSettings}
            status={cycle.status}
            measurement={measurement}
            channelsIot={cycle.channelsIot}
            testChannel={canTestChannel ? testChannel : undefined}
          />
        )}
      </div>
      <Modal
        modalTitle={t('cycle.modals.delete.title')}
        isVisible={isDeletionCycle}
        content={t('cycle.modals.delete.description', '', { name: cycle.name })}
        acceptBtnText={t('buttons.delete')}
        onClose={closeModalDeletion}
        onAccept={acceptDeleteCycle}
        showAcceptBtn
        showCancelBtn
      />
      <Modal
        modalTitle={t('cycle.modals.attachments.title')}
        isVisible={isAttachmentModalOpen}
        onClose={onAttachmentModalClose}
        onAccept={saveCycleAttachment}
        acceptBtnText={`${attachmentSaving ? t('buttons.saving') : t('buttons.save')}`}
        className="min-h-[500px] w-[800px]"
      >
        <CycleAttachment onSelectFile={setSelectedAttachment} />
      </Modal>
      {isViewSettings && <CycleSettings isVisible={isViewSettings} onClose={closeSettings} cycleId={cycle.id} cycleName={cycle.name} />}
      <DuplicateCycleModal
        cycleId={cycle.id}
        cycleName={cycle.name}
        isVisible={isDuplicateModalOpen}
        onClose={() => setIsDuplicateModalOpen(false)}
        onDuplicated={() => getCycles({ screenId, contractId, query: { sort } })}
      />
      {missingCalCertControllers && (
        <CalCertWarningModal
          isVisible
          controllers={missingCalCertControllers}
          onClose={closeCalCertModal}
          onDisableCheck={disableCalCertCheckAndRetry}
          onProceedWithoutCalCert={proceedWithoutCalCert}
          proceedButtonText="Start without Cal-Cert"
        />
      )}
    </>
  );
};

const mapDispatch = (dispatch: Dispatch) => ({
  getCycles: dispatch.cycles.getListByContractAndScreen,
  deleteCycle: dispatch.cycles.deleteCycle,
  selectCycle: dispatch.cycles.setCycle,
  saveAttachment: dispatch.cycles.saveAttachment,
  getAttachments: dispatch.cycles.getAttachments,
  getChannels: dispatch.channels.getChannels,
  getMode: dispatch.cycles.getSettingsMode,
  getModeSettings: dispatch.cycles.getSettingsByMode,
  setIsViewSettingsGlobal: dispatch.cycles.setIsViewSettings,
  updateChannels: dispatch.channels.updateChannels,
  updateCycle: dispatch.cycles.updateCycle,
  updateController: dispatch.controllers.updateController,
});

const mapState = (state: RootState) => ({
  measurement: getMeasurementPreference(state.contracts.contract, state.locations?.currentLocation),
  user: state.user.profile,
});

export default connect<StateProps, DispatchProps, ComponentProps>(mapState, mapDispatch)(CycleV2);
